import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {hpcAttrs} from '@github-ui/hpc'
import {VIEW_NAMES} from '@github-ui/issue-url-helper/constants/view-constants'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
import {LABELS} from '@github-ui/issues-shared/labels'
import {VALUES} from '@github-ui/issues-shared/values'
import {invalidateOpenClosedCounts} from '@github-ui/issues-tab-counter/open-closed-count'
import {ListView} from '@github-ui/list-view'
import {IssueRow} from '@github-ui/list-view-items-issues-prs/IssueRow'
import {NoResults} from '@github-ui/list-view-items-issues-prs/NoResults'
import {PullRequestRow} from '@github-ui/list-view-items-issues-prs/PullRequestRow'
import {checkIfQuerySupportsPr, isPrsOnly} from '@github-ui/list-view-items-issues-prs/Query'
import {getSelectedSortOptionKeyFromQuery, isReactionSort} from '@github-ui/list-view-items-issues-prs/SortingDropdown'
import {useIssueIndexSecondaryQuery} from '@github-ui/list-view-items-issues-prs/use-issue-index-secondary-query'
import {noop} from '@github-ui/noop'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {NavigateOptions, To} from '@github-ui/react-router'
import {useLocation} from '@github-ui/react-router'
import type {ReportableError} from '@github-ui/relay-preloaded-query-boundary'
import {useRelayCachePreheatContext} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {repoSidebarLinks} from '@github-ui/repos-issues-sidebar-ui/repos-issues-sidebar-links'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {type AnalyticsEvent, useAnalytics} from '@github-ui/use-analytics'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {useNavigate} from '@github-ui/use-navigate'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useUser} from '@github-ui/use-user'
import {Pagination} from '@primer/react'
import {clsx} from 'clsx'
import {
  memo,
  type MouseEvent,
  type RefObject,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {graphql, usePaginationFragment, useRelayEnvironment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import {usePreviewQueryContext} from '../../contexts/PreviewQueryContext'
import {useActiveSearchQuery, useCurrentPage, useQueryBulk, useSetCurrentPage} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {setForwardSkipOffset} from '../../hooks/use-load-more-nav-items'
import type {IssueSearchType} from '../../types/issue-search-type'
import {getPageNumberFromUrlQuery, replacePageNumberInUrl} from '../../utils/urls'
import type {
  ListItemsPaginated_results$data,
  ListItemsPaginated_results$key,
} from './__generated__/ListItemsPaginated_results.graphql'
import type {SearchPaginatedQuery} from './__generated__/SearchPaginatedQuery.graphql'
import {ListItemsHeader} from './header/ListItemsHeader'
import {useShiftKey} from './hooks/use-shift-key'
import styles from './ListItems.module.css'
import {MoreResultsAvailableBanner} from './MoreResultsAvailableBanner'

const NOOP_FOCUS_COMMANDS = {'issues-react:focus-next-issue': noop, 'issues-react:focus-previous-issue': noop} as const

type ListProps = {
  search: ListItemsPaginated_results$key
  queryFromCustomView?: string | null
  listRef: RefObject<HTMLUListElement | undefined>
  isBulkSupported: boolean
  issueTypesEnabled?: boolean
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onIssueNavigate?: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  searchType?: IssueSearchType
  preheatEnabled?: boolean
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ListItemsFragment = graphql`
  fragment ListItemsPaginated_results on Searchable
  @argumentDefinitions(
    cursor: {type: "String", defaultValue: null}
    query: {type: "String!"}
    first: {type: "Int"}
    labelPageSize: {type: "Int!"}
    skip: {type: "Int", defaultValue: null}
    fetchRepository: {type: "Boolean!"}
    type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
    showIssueFieldPills: {type: "Boolean!"}
  )
  @refetchable(queryName: "SearchPaginatedQuery") {
    search(first: $first, after: $cursor, query: $query, type: $type, skip: $skip)
      @catch(to: RESULT)
      @stream_connection(key: "Query_search", initial_count: 12) {
      edges @required(action: THROW) {
        node {
          ... on Issue {
            id
            __typename
            number
            repository {
              name
              owner {
                login
              }
            }
            ...IssueRow
              @alias(as: "issueRow")
              @arguments(
                labelPageSize: $labelPageSize
                fetchRepository: $fetchRepository
                showIssueFieldPills: $showIssueFieldPills
              )
            # eslint-disable-next-line relay/must-colocate-fragment-spreads
            ...ApplyAssigneesBulkActionIssueFragment @alias(as: "applyAssignees")
          }
          ... on PullRequest {
            id
            __typename
            number
            repository {
              name
              owner {
                login
              }
            }
            ...PullRequestRow @alias(as: "pullRequestRow") @arguments(labelPageSize: $labelPageSize)
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
      issueCount
      issueSearchType
    }
  }
`

// PascalCase + props-object shape would make the React Compiler classify this as
// a component instead of a hook. Keep the `use` prefix and call it as a hook so the
// compiler allocates `usePaginationFragment`'s sub-hooks against the parent fiber
// rather than spinning up a separate memoCache.
function useSearchPagination(searchKey: ListItemsPaginated_results$key) {
  const {data, refetch} = usePaginationFragment<SearchPaginatedQuery, ListItemsPaginated_results$key>(
    ListItemsFragment,
    searchKey,
  )

  return {data, refetch}
}

type SearchPaginationResult = ReturnType<typeof useSearchPagination>
// The `value` payload of a successful @catch result, extracted via the `{ok: true}` branch.
type SearchValue = Extract<NonNullable<SearchPaginationResult['data']['search']>, {ok: true}>['value']

// ---------------------------------------------------------------------------
// RowItem — memo'd wrapper so IssueRow/PullRequestRow React.memo is effective.
//
// The callbacks (onSelect, onNavigate) reference state that changes frequently
// (checkedItems, handleNavigate). Passing them as plain function props would
// create new references on every ListItems render, defeating the memo wrapping
// on IssueRow and PullRequestRow. Using MutableRefs lets us update the live
// function without changing the ref object identity, so RowItem (and its
// memo'd children) only re-render when props that truly affect the UI change.
// ---------------------------------------------------------------------------

type RowItemProps = {
  node: IssueNodeType | PullRequestNodeType
  index: number
  isSelected: boolean
  listItemMetadata: Parameters<typeof IssueRow>[0]['listItemMetadata']
  onSelectRow: Parameters<typeof IssueRow>[0]['onSelectRow']
  getMetadataHref: Parameters<typeof IssueRow>[0]['getMetadataHref']
  getAdditiveMetadataHref: Parameters<typeof IssueRow>[0]['getAdditiveMetadataHref']
  getIssueFieldHref: Parameters<typeof IssueRow>[0]['getIssueFieldHref']
  onSidePanelNavigate?: Parameters<typeof IssueRow>[0]['onSidePanelNavigate']
  sortingItemSelected: string
  scopedRepository: Parameters<typeof IssueRow>[0]['scopedRepository']
  navigate: (to: To) => void
  itemSelectedRef: RefObject<(id: string, node: ItemNodeType, selected: boolean) => void>
  handleNavigateRef: RefObject<(to: To, options?: NavigateOptions, position?: number) => void>
  preheatEnabled: boolean
}

function RowItemComponent({
  node,
  index,
  isSelected,
  listItemMetadata,
  onSelectRow,
  getMetadataHref,
  getAdditiveMetadataHref,
  getIssueFieldHref,
  onSidePanelNavigate,
  sortingItemSelected,
  scopedRepository,
  navigate,
  itemSelectedRef,
  handleNavigateRef,
  preheatEnabled,
}: RowItemProps) {
  const onSelect = useCallback(
    (selected: boolean) => itemSelectedRef.current(node.id, node, selected),
    [node, itemSelectedRef],
  )

  const onNavigateIssue = useCallback(
    (to: To, options?: NavigateOptions) => handleNavigateRef.current(to, options, index),
    [handleNavigateRef, index],
  )

  return (
    <div className={styles.listItem}>
      {node.__typename === 'Issue' && node.issueRow && (
        <IssueRow
          issueKey={node.issueRow}
          listItemMetadata={listItemMetadata}
          isActive={false}
          isSelected={isSelected}
          getMetadataHref={getMetadataHref}
          onSelect={onSelect}
          onSelectRow={onSelectRow}
          onNavigate={onNavigateIssue}
          onSidePanelNavigate={onSidePanelNavigate}
          sortingItemSelected={sortingItemSelected}
          scopedRepository={scopedRepository}
          data-testid={TEST_IDS.issueRowItem(node.number)}
          getAdditiveMetadataHref={getAdditiveMetadataHref}
          getIssueFieldHref={getIssueFieldHref}
          preheatEnabled={preheatEnabled}
        />
      )}
      {node.__typename === 'PullRequest' && node.pullRequestRow && (
        <PullRequestRow
          pullRequestKey={node.pullRequestRow}
          listItemMetadata={listItemMetadata}
          onNavigate={navigate}
          isActive={false}
          isSelected={isSelected}
          getMetadataHref={getMetadataHref}
          onSelect={onSelect}
          onSelectRow={onSelectRow}
          sortingItemSelected={sortingItemSelected}
          data-testid={TEST_IDS.pullRequestRowItem(node.number)}
        />
      )}
    </div>
  )
}

const RowItem = memo(RowItemComponent)

const getCurrentViewName = ({activeSearchQuery}: {activeSearchQuery: string}) => {
  const trimmedQuery = activeSearchQuery.trim()
  const matchedLink = repoSidebarLinks.find(link => link.query === trimmedQuery)
  return matchedLink?.name ?? VIEW_NAMES.repository
}
/**
 * See ListView stories for a representation of this component.
 * packages/list-view/src/stories/RecentActivity/RecentActivity.stories.tsx
 * https://ui.githubapp.com/storybook/?path=/story/recipes-list-view-dotcom-pages--recent-activity
 * https://ui.githubapp.com/storybook/?path=/story/recipes-list-view-dotcom-pages--repository-issues
 */

/* A list of issues or pull requests that can be rendered in the sidebar or in the main content area.
 * @param {ItemIdentifier} itemIdentifier - Identifier for the current item displayed in the viewer (when the list is
      present in the sidebar). Undefined when the viewer is closed
 * @param {string} queryFromCustomView - The query corresponding to the custom view (if any)
 */
export function ListItems(props: ListProps) {
  // Gate component: keep the hook list here as small and stable as possible so a
  // render-time `throw` cannot perturb hook ordering for the rest of the body.
  // All other hooks live in `ListItemsImpl`. See https://github.com/github/github-ui/pull/20418
  // for the bug this split prevents (hook-order mismatch under the React Compiler).
  const {search} = props
  const {data: pageData, refetch} = useSearchPagination(search)
  const searchResult = pageData.search

  if (searchResult && !searchResult.ok) {
    const errors = searchResult.errors
    const isServiceUnavailable = errors?.some(
      e => e && typeof e === 'object' && 'type' in e && e.type === 'SERVICE_UNAVAILABLE',
    )

    const errorMessage = `ListItemsPaginated: pageData.search.value.edges is errored. Errors: ${JSON.stringify(errors)}`
    const error: ReportableError = new Error(errorMessage)
    error.shouldSkipReport = isServiceUnavailable ?? false
    throw error
  }

  // After the throw, `searchResult` is `{ok: true, value} | null | undefined` — pass the
  // already-narrowed payload to the impl so it doesn't need to re-discriminate the union.
  return (
    <ListItemsImpl
      {...props}
      searchValue={searchResult?.value}
      searchDataMissing={searchResult == null}
      pageDataId={pageData.id}
      refetch={refetch}
    />
  )
}

type ListItemsImplProps = Omit<ListProps, 'search'> & {
  searchValue: SearchValue | undefined
  searchDataMissing: boolean
  pageDataId: string | undefined
  refetch: SearchPaginationResult['refetch']
}

function ListItemsImpl({
  queryFromCustomView,
  listRef,
  isBulkSupported,
  issueTypesEnabled,
  onSidePanelNavigate,
  onIssueNavigate,
  updateIssueSearchType,
  onListItemsChange,
  searchType,
  searchValue,
  searchDataMissing,
  pageDataId,
  refetch,
  preheatEnabled = true,
}: ListItemsImplProps) {
  // Read the URL via React Router's reactive hook so the React Compiler treats
  // `path` as a reactive dependency. Reading from `ssrSafeLocation` (a module
  // import) gave the pagination callbacks below no reactive deps, so the
  // compiler froze `handlePageChange`/`buildPageHref` with the first render's
  // path forever — causing pagination links to drop the active `q` after a
  // route change (e.g. switching to the Closed issues tab).
  const {pathname, search: urlSearch} = useLocation()
  const path = `${pathname}${urlSearch}`

  const {scoped_repository, best_match_sort_enabled} = useAppPayload<AppPayload>()
  const {currentUser} = useUser()
  const activeSearchQuery = useActiveSearchQuery()
  const currentPage = useCurrentPage()
  const setCurrentPage = useSetCurrentPage()
  const currentViewName = getCurrentViewName({activeSearchQuery})
  const navigate = useNavigate()
  const isMac = useIsPlatform(['mac'])

  const [lastSelectedItem, setLastSelectedItem] = useState<{id: string; node: ItemNodeType}>()
  const {shiftKeyPressedRef} = useShiftKey()
  const environment = useRelayEnvironment()

  const {edges, issueCount, issueSearchType} = searchValue ?? {}
  const hasValidEdges = edges !== null && edges !== undefined
  const searchResultsReady = !searchDataMissing && hasValidEdges

  // just to make sure we don't refetch the data more than once
  const [refetched, setRefetched] = useState(false)

  useEffect(() => {
    announce(LABELS.numberOfResults(issueCount || 0), {assertive: true})
  }, [issueCount])

  useEffect(() => {
    updateIssueSearchType?.(issueSearchType)
  }, [issueSearchType, updateIssueSearchType])

  // Workaround until we find a way to fix an issue where the data is null when we have the data in the browser cache
  // but it's marked as stale by Relay. See https://github.com/github/issues/issues/13005
  useEffect(() => {
    const hasValidParentNode = pageDataId && pageDataId !== 'RootQueryObject'
    const shouldRefetch = !refetched && searchDataMissing && hasValidParentNode
    if (!shouldRefetch) return

    const disposable = refetch({}, {fetchPolicy: 'network-only', onComplete: () => setRefetched(true)})

    // cleanup
    return disposable.dispose
  }, [activeSearchQuery, refetch, refetched, searchDataMissing, pageDataId])

  const data = useMemo(
    () =>
      edges
        ? edges
            .map(edge =>
              edge?.node && (edge.node.__typename === 'PullRequest' || edge.node.__typename === 'Issue')
                ? edge.node
                : null,
            )
            .filter(node => node != null)
        : [],
    [edges],
  )

  // Register fetchQuery as the bulk job success callback so the list re-queries on completion
  const {registerBulkJobSuccess} = useQueryBulk()

  useEffect(() => {
    if (!onListItemsChange) return
    const navigableItems: ItemIdentifier[] = data
      .map(node => {
        if (!node || !node.repository) return null
        if (node.__typename !== 'Issue' && node.__typename !== 'PullRequest') return null
        return {
          number: node.number,
          owner: node.repository.owner.login,
          repo: node.repository.name,
          type: node.__typename,
        }
      })
      .filter((item): item is ItemIdentifier => item !== null)
    onListItemsChange(navigableItems)
  }, [data, onListItemsChange])

  const paginationLoadingRef = useRef<HTMLDivElement>(null)

  const onlyPrs = useMemo(() => {
    return isPrsOnly(activeSearchQuery)
  }, [activeSearchQuery])

  const maxItems = VALUES.maxIssuesListItems(onlyPrs, !!currentUser)

  const totalPages = useMemo(() => {
    if (!issueCount) {
      return 0
    }

    const cappedIssueCount = Math.min(maxItems, issueCount)
    return Math.ceil(cappedIssueCount / VALUES.issuesPageSize(urlSearch))
  }, [issueCount, urlSearch, maxItems])

  const {getQueryFieldUrl, getAdditiveQueryFieldUrl, getIssueFieldUrl, navigateToUrl} = useAppNavigate()
  const {resetQueue} = useRelayCachePreheatContext()
  const previewQuery = usePreviewQueryContext()

  const handlePreviewListClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!previewQuery) return
      // In the saved view live preview, anchors carrying a `q=` param (filter
      // chips, Open/Closed tabs) update the parent's draft query instead of
      // navigating. Modifier-clicks still open the URL normally.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a')
      if (!anchor) return
      const rawHref = anchor.getAttribute('href')
      if (!rawHref) return
      if (!ssrSafeWindow) return
      const url = new URL(rawHref, ssrSafeWindow.location.href)
      const nextQuery = url.searchParams.get('q')
      if (nextQuery === null) return
      event.preventDefault()
      event.stopPropagation()
      startTransition(() => {
        previewQuery.onQueryChange(nextQuery)
      })
    },
    [previewQuery],
  )

  const handlePageChange = useCallback(
    (e: MouseEvent, page_number: number) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? e.metaKey : e.ctrlKey
      if (isMetaKey || e.shiftKey) {
        return
      }
      e.preventDefault()

      resetQueue()
      const updatedUrl = replacePageNumberInUrl(path, page_number)
      setCurrentPage(page_number)
      navigateToUrl(updatedUrl)
    },
    [isMac, path, setCurrentPage, navigateToUrl, resetQueue],
  )

  const buildPageHref = useCallback(
    (page_number: number) => {
      return replacePageNumberInUrl(path, page_number)
    },
    [path],
  )

  const initialSortingItem = useMemo(() => {
    const selectedKey = getSelectedSortOptionKeyFromQuery(
      queryFromCustomView || activeSearchQuery,
      best_match_sort_enabled,
    )

    return selectedKey || 'created'
  }, [activeSearchQuery, queryFromCustomView, best_match_sort_enabled])

  const [sortingItemSelected, setSortingItemSelected] = useState<string>(initialSortingItem)
  const [listHasPRs, setListHasPRs] = useState(false)

  const {sendAnalyticsEvent} = useAnalytics()

  const useBulkActions = useMemo(() => {
    return isBulkSupported && !checkIfQuerySupportsPr(activeSearchQuery)
  }, [activeSearchQuery, isBulkSupported])

  const [checkedItems, setCheckedItems] = useState(() => new Set<string>())

  useEffect(() => {
    registerBulkJobSuccess(() => {
      refetch({}, {fetchPolicy: 'store-and-network'})
      environment.commitUpdate(store => {
        invalidateOpenClosedCounts(store)
      })
    })
    return () => {
      registerBulkJobSuccess(null)
    }
  }, [refetch, registerBulkJobSuccess, environment])

  const updateListHasPRs = useCallback(
    (checkedSet: Set<string>) => {
      const hasAnyPR = data.some(item => item != null && checkedSet.has(item.id) && item.__typename === 'PullRequest')
      setListHasPRs(hasAnyPR)
    },
    [data],
  )

  const applyShiftSelection = useCallback(
    (newCheckedItems: Set<string>, fromId: string, toId: string, selected: boolean) => {
      const fromIndex = data.findIndex(d => d.id === fromId)
      const toIndex = data.findIndex(d => d.id === toId)
      if (fromIndex === -1 || toIndex === -1) return // handle cases where we've removed issues from the list
      for (let i = Math.min(fromIndex, toIndex); i <= Math.max(fromIndex, toIndex); i++) {
        const d = data[i] as ItemNodeType
        if (selected) {
          newCheckedItems.add(d.id)
        } else {
          newCheckedItems.delete(d.id)
        }
      }
      updateListHasPRs(newCheckedItems)
    },
    [data, updateListHasPRs],
  )

  const itemSelected = useCallback(
    (id: string, node: ItemNodeType, selected: boolean) => {
      const newCheckedItems = new Set<string>(checkedItems)

      if (shiftKeyPressedRef.current && lastSelectedItem) {
        applyShiftSelection(newCheckedItems, lastSelectedItem.id, id, selected)
      } else {
        if (selected) {
          newCheckedItems.add(id)
        } else {
          newCheckedItems.delete(id)
        }
        updateListHasPRs(newCheckedItems)
      }

      setCheckedItems(newCheckedItems)
      setLastSelectedItem({id, node})
    },
    [applyShiftSelection, checkedItems, lastSelectedItem, shiftKeyPressedRef, updateListHasPRs],
  )

  useEffect(() => {
    const pageNumber = getPageNumberFromUrlQuery(urlSearch)
    setCurrentPage(pageNumber > 1 ? pageNumber : 1)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCheckedItems(new Set<string>())
  }, [setCurrentPage, urlSearch])

  // Stable array reference — only recomputed when `data` changes, not on every render.
  const issueNodes = useMemo(
    () =>
      data.reduce((arr, node) => {
        if (node != null) arr.push(node)
        return arr
      }, new Array<ItemNodeType>()),
    [data],
  )

  const listItemsHeader = (
    <ListItemsHeader
      checkedItems={checkedItems}
      issueCount={issueCount || 0}
      issueNodes={issueNodes}
      sortingItemSelected={sortingItemSelected}
      setCheckedItems={setCheckedItems}
      setSortingItemSelected={setSortingItemSelected}
      useBulkActions={useBulkActions}
      setCurrentPage={setCurrentPage}
      listHasPRs={listHasPRs}
      updateListHasPRs={updateListHasPRs}
      issueTypesEnabled={issueTypesEnabled}
      searchType={searchType}
    />
  )

  const onSelectRow = useCallback(
    (payload?: {[key: string]: unknown} | AnalyticsEvent | undefined) => {
      sendAnalyticsEvent('search_results.select_row', 'SEARCH_RESULT_ROW', {...payload})
    },
    [sendAnalyticsEvent],
  )

  const currentPageNodes = data

  const currentPageNodeTypes = useMemo(
    () => new Set(currentPageNodes?.map(node => node?.__typename) || []),
    [currentPageNodes],
  )
  const handleNavigate = useCallback(
    (to: To, options = {}, position?: number) => {
      onIssueNavigate?.(position)
      if (isFeatureEnabled('issues_react_jk_nav')) {
        const navigableItems = data.reduce<ItemIdentifier[]>((acc, node) => {
          if (node && node.repository && node.__typename === 'Issue') {
            acc.push({
              number: node.number,
              owner: node.repository.owner.login,
              repo: node.repository.name,
              type: node.__typename,
            })
          }
          return acc
        }, [])
        const scopedQuery = scoped_repository
          ? `repo:${scoped_repository.owner}/${scoped_repository.name} ${activeSearchQuery}`
          : activeSearchQuery

        const backItemCount = ((currentPage ?? 1) - 1) * VALUES.issuesPageSize(urlSearch)

        // Set the skip offset so loadMore knows where to start fetching
        setForwardSkipOffset(backItemCount + navigableItems.length)

        const scrollY = ssrSafeWindow?.scrollY
        const navState = {
          viewNav: {
            viewName: currentViewName,
            items: navigableItems,
            returnUrl: path,
            scrollY,
            totalCount: issueCount ?? navigableItems.length,
            searchQuery: scopedQuery,
            backItemCount,
          },
        }
        return navigateToUrl(to, {...options, state: navState}, true)
      }
      return navigateToUrl(to, options, true)
    },
    [
      navigateToUrl,
      onIssueNavigate,
      data,
      path,
      issueCount,
      activeSearchQuery,
      scoped_repository,
      currentPage,
      currentViewName,
      urlSearch,
    ],
  )

  // Refs keep the callback identities stable so RowItem (and its memo'd children)
  // don't re-render just because checkedItems or handleNavigate changed identity.
  const itemSelectedRef = useRef(itemSelected)
  const handleNavigateRef = useRef(handleNavigate)
  useEffect(() => {
    itemSelectedRef.current = itemSelected
    handleNavigateRef.current = handleNavigate
  })

  const [deletedRecordId] = useSessionStorage<string>(
    `${scoped_repository?.owner}-${scoped_repository?.name}-deletedRecordId`,
    '',
  )
  const nodes = useMemo(
    () =>
      data
        .map(node => node?.id)
        .filter(id => id !== deletedRecordId)
        .filter(Boolean),
    [data, deletedRecordId],
  )

  const issueIndexLazyData = useIssueIndexSecondaryQuery({
    nodes,
    includeReactions: isReactionSort(sortingItemSelected),
  })

  const items = currentPageNodes?.map((node, index) => {
    if (node == null || node.id === deletedRecordId) {
      return null
    }

    return (
      <RowItem
        key={node.id}
        node={node}
        index={index}
        isSelected={checkedItems.has(node.id)}
        listItemMetadata={issueIndexLazyData}
        getMetadataHref={getQueryFieldUrl}
        getAdditiveMetadataHref={getAdditiveQueryFieldUrl}
        getIssueFieldHref={getIssueFieldUrl}
        onSelectRow={onSelectRow}
        onSidePanelNavigate={onSidePanelNavigate}
        sortingItemSelected={sortingItemSelected}
        scopedRepository={scoped_repository}
        navigate={navigate}
        itemSelectedRef={itemSelectedRef}
        handleNavigateRef={handleNavigateRef}
        preheatEnabled={preheatEnabled}
      />
    )
  })

  const showMoreResultsAvailableBanner =
    currentPage !== undefined && totalPages === currentPage && issueCount !== undefined && issueCount > maxItems

  const focusTabbableIssue = () => {
    if (listRef.current) {
      // either the first issue in the list or the last tabbed item have a tabindex of 0, while the rest have -1
      // this is the primer implementation of navigating the list with tabs / keys
      const tabbableItem = listRef.current.querySelector('[tabindex="0"]')
      if (tabbableItem && tabbableItem instanceof HTMLElement) {
        tabbableItem.focus()
      }
    }
  }

  const list = (
    <>
      <GlobalCommands
        commands={{
          'issues-react:focus-next-issue': focusTabbableIssue,
          'issues-react:focus-previous-issue': focusTabbableIssue,
        }}
      />
      <div
        data-testid="list-load-progress-bar"
        ref={paginationLoadingRef}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx('turbo-progress-bar', styles.progressBar)}
      />
      {/* if we already have focus in the list, delegate keyboard navigation to ListView */}
      <ScopedCommands commands={NOOP_FOCUS_COMMANDS} className={styles.listScopedCommand}>
        <ListView
          {...testIdProps(TEST_IDS.list)}
          title={LABELS.searchResults}
          totalCount={issueCount || 0}
          selectedCount={checkedItems.size}
          titleHeaderTag="h2"
          isSelectable={useBulkActions}
          metadata={listItemsHeader}
          singularUnits={LABELS.singularUnits(currentPageNodeTypes)}
          pluralUnits={LABELS.pluralUnits(currentPageNodeTypes)}
          listRef={listRef}
          strictFocusZone={false}
        >
          {items}
          {items.length === 0 && searchResultsReady && <NoResults />}
          {showMoreResultsAvailableBanner && (
            <MoreResultsAvailableBanner itemsLabel={LABELS.pluralUnits(currentPageNodeTypes) ?? 'issues'} />
          )}
        </ListView>
      </ScopedCommands>
    </>
  )

  return (
    <div>
      <div
        {...hpcAttrs()}
        className={styles.listContainer}
        onClickCapture={previewQuery ? handlePreviewListClick : undefined}
      >
        {list}
      </div>
      {currentPage && totalPages > 1 ? (
        <Pagination
          pageCount={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          hrefBuilder={buildPageHref}
          marginPageCount={2}
          surroundingPageCount={2}
        />
      ) : null}
    </div>
  )
}

type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends ReadonlyArray<infer ElementType> ? ElementType : never

type NodeType = NonNullable<
  NonNullable<
    ArrayElement<NonNullable<Extract<ListItemsPaginated_results$data['search'], {ok: true}>['value']['edges']>>
  >['node']
>

type PullRequestNodeType = Extract<NodeType, {__typename: 'PullRequest'}>

export type IssueNodeType = Extract<NodeType, {__typename: 'Issue'}>
export type ItemNodeType = IssueNodeType | PullRequestNodeType
